import { Canceler } from 'axios';
import { Box, Radio, RadioGroup, FormControlLabel } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FixedTab, RouteTabs, VariableTab } from '../../../components/RouteTabs';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { ICollectionCreator } from '../../../services/types';
import { replace } from '../../../services/utils';
import { RouteNames } from '../../config';
import AutomatedCollectionList from './AutomatedCollectionList';
import AutomatedCollectionView from './AutomatedCollectionView';
import { getAutomatedCollections, toggleActiveAutomatedCollection, deleteAutomatedCollection } from '../../../services/api/AutomatedCollection';
import useStyles from './styles';

interface IProps {}

interface IParamProps {
  trackingId?: string;
}

const AutomatedCollection = (props: IProps & RouteComponentProps): JSX.Element => {
  const classes = useStyles();

  const path = RouteNames.AutomatedCollection;
  const match = useRouteMatch<IParamProps>(`${path}/v/:trackingId`);

  const history = useHistory();
  const location = useLocation();
  const trackingId = match?.params?.trackingId;

  const initialTrackingIds = trackingId ? [trackingId] : [];
  const [editingTrackingIds, setEditingTrackingIds] = useState<string[]>(initialTrackingIds);
  const [isOnboarding, setIsOnboarding] = useState<string>('');

  const [automatedCollections, setAutomatedCollections] = useState<ICollectionCreator[]>();

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const canceler = useRef<Canceler>(() => undefined);

  const handleError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.message}`);
    },
    [snackbar],
  );

  const handleIsOnboardingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsOnboarding(e.target.value);
  }, []);

  const fetchAutomatedCollections = async () => {
    canceler.current();

    try {
      const [fetchPromise, cancel] = getAutomatedCollections(isOnboarding === '' ? undefined : Boolean(Number(isOnboarding)));
      canceler.current = cancel;

      const result = await fetchPromise;
      setAutomatedCollections(result.data.collectionCreators);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (location.pathname === RouteNames.AutomatedCollection) {
        if (!automatedCollections) {
          fetchAutomatedCollections();
        }
      }
    };

    fetchData();

    return () => canceler.current();
  }, [location.pathname, automatedCollections]);

  useEffect(() => {
    if (automatedCollections) {
      fetchAutomatedCollections();
    }
  }, [isOnboarding]);

  const handleRowClick = useCallback(
    (ac: ICollectionCreator) => {
      if (!ac || !ac.trackingId) return;

      const { trackingId } = ac;

      if (!editingTrackingIds.find(id => id === trackingId)) {
        setEditingTrackingIds([...editingTrackingIds, trackingId]);
      }
    },
    [editingTrackingIds],
  );

  const handleActivationChange = useCallback(
    (ac: ICollectionCreator) => {
      const onProceed = async () => {
        if (!automatedCollections || !ac || !ac.trackingId) return;

        const idx = automatedCollections.findIndex(c => c.trackingId === ac.trackingId);

        try {
          backdrop.show();
          const result = await toggleActiveAutomatedCollection(ac.trackingId);

          if (result.status === 200) {
            const cloned = [...automatedCollections];
            cloned[idx].isActive = !cloned[idx].isActive;
            cloned[idx].nextAt = result.data.nextAt;

            setAutomatedCollections(cloned);
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          console.error(e);
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: `${ac.isActive ? 'Stop' : 'Start'}`,
        text: `Are you sure you want to ${ac.isActive ? 'stop' : 'start'} this collection?`,
        onProceed: () => {
          onProceed();
        },
      });
    },
    [backdrop, confirm, snackbar, automatedCollections],
  );

  const handleDeleteClick = useCallback(
    (ac: ICollectionCreator) => {
      if (ac.isActive) {
        return;
      }

      const onProceed = async () => {
        if (!automatedCollections || !ac.trackingId) return;

        const idx = automatedCollections.findIndex(c => c.trackingId === ac.trackingId);

        try {
          backdrop.show();
          const result = await deleteAutomatedCollection(ac.trackingId);

          if (result.status === 200) {
            const cloned = [...automatedCollections];
            cloned.splice(idx, 1);

            setAutomatedCollections(cloned);

            snackbar.success('Deleted successfully.');
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          console.error(e);
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: 'Delete',
        text: 'Are you sure you want to delete this collection?',
        onProceed,
      });
    },
    [backdrop, confirm, snackbar, automatedCollections],
  );

  const handleCreate = useCallback(
    data => {
      if (automatedCollections) {
        setAutomatedCollections([data, ...automatedCollections]);
      } else {
        fetchAutomatedCollections();
      }

      history.push(RouteNames.AutomatedCollection);

      snackbar.success('Saved successfully.');
    },
    [history, snackbar, automatedCollections],
  );

  const handleTabClose = useCallback(
    trackingId => {
      setEditingTrackingIds(editingTrackingIds.filter(id => id !== trackingId));
    },
    [editingTrackingIds],
  );

  const getTabLabel = useCallback(
    trackingId => {
      const c = automatedCollections?.find(c => c.trackingId === trackingId);
      const label = c ? `${c.internalTitle ? `${c.internalTitle} : ` : ''}${c.title}` : `VIEW ${trackingId}`;

      return label;
    },
    [automatedCollections],
  );

  const handleEdit = useCallback(
    data => {
      if (automatedCollections) {
        const replaced = replace(automatedCollections, ac => ac.trackingId === data.trackingId, data);
        setAutomatedCollections(replaced);
      }

      snackbar.success('Saved changes successfully.');
    },
    [automatedCollections, snackbar],
  );

  const getLinkUri = useCallback((trackingId?: string) => (trackingId ? `${RouteNames.AutomatedCollection}/v/${trackingId}` : ''), []);

  const getTabUri = useCallback(trackingId => `v/${trackingId}`, []);

  const keyExtractor = useCallback(trackingId => `tab-floating-${trackingId}`, []);

  const variableTabRenderer = useCallback(
    trackingId => <AutomatedCollectionView trackingId={trackingId} onCreated={handleEdit} onError={handleError} />,
    [handleEdit, handleError],
  );

  return (
    <div>
      <RouteTabs path={path}>
        <FixedTab label="All Automated Collections">
          <Box>
            <Box className={classes.optionsContainer}>
              <RadioGroup name="isOnboarding" value={isOnboarding} onChange={handleIsOnboardingChange} className={classes.optionsRadioButtonGroups}>
                <FormControlLabel value={''} control={<Radio color="primary" />} label="All Collections" />
                <FormControlLabel value={'0'} control={<Radio color="primary" />} label="Exclude Onboarding Collections" />
                <FormControlLabel value={'1'} control={<Radio color="primary" />} label="Onboarding Collections Only" />
              </RadioGroup>
            </Box>
            <AutomatedCollectionList
              collectionCreators={automatedCollections || []}
              onClickRow={handleRowClick}
              onChangeActivation={handleActivationChange}
              onClickDeleteButton={handleDeleteClick}
              getLinkUri={getLinkUri}
            />
          </Box>
        </FixedTab>
        <FixedTab label="Create an Automated Collection" uri="create">
          <AutomatedCollectionView onCreated={handleCreate} onError={handleError} />
        </FixedTab>
        <VariableTab<string>
          data={editingTrackingIds}
          label={getTabLabel}
          uri={getTabUri}
          onClose={handleTabClose}
          keyExtractor={keyExtractor}
          render={variableTabRenderer}
        />
      </RouteTabs>
    </div>
  );
};

export default AutomatedCollection;
